// Builds the monthly data report as a list of blocks that are rendered on screen
// (MonthlyReport page) and exported to Word/PDF (monthlyReportExport.js).
//
// Block types:
//   { type: 'title' | 'subtitle', text }
//   { type: 'paragraph', runs }                      runs: [{ text, bold? }]
//   { type: 'list', items }                          items: [{ runs, children?: items }]
//   { type: 'table', widths, rows, header?, small? } widths: fractions of the page width

const FIRST_REPORT_MONTH = '2024-01';

const toMonthKey = (year, month) => `${year}-${String(month).padStart(2, '0')}`;

const parseMonth = (monthKey) => {
  const [year, month] = monthKey.split('-').map(Number);
  return { year, month };
};

const utcDate = (year, month, day) => new Date(Date.UTC(year, month - 1, day));

const formatDay = (date) =>
  date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });

export const formatMonth = (monthKey) => {
  const { year, month } = parseMonth(monthKey);
  return utcDate(year, month, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
};

const monthName = (monthKey) => {
  const { year, month } = parseMonth(monthKey);
  return utcDate(year, month, 1).toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' });
};

/** Months available in the dropdown, newest first. The current month is marked in progress. */
export const getReportMonths = () => {
  const now = new Date();
  const current = toMonthKey(now.getFullYear(), now.getMonth() + 1);
  const months = [];
  let year = now.getFullYear();
  let month = now.getMonth() + 1;
  while (toMonthKey(year, month) >= FIRST_REPORT_MONTH) {
    const key = toMonthKey(year, month);
    months.push({ value: key, label: key === current ? `${formatMonth(key)} (in progress)` : formatMonth(key) });
    month -= 1;
    if (month === 0) {
      month = 12;
      year -= 1;
    }
  }
  return months;
};

// First and last day covered by the report; the current month ends today.
const reportPeriod = (monthKey) => {
  const { year, month } = parseMonth(monthKey);
  const now = new Date();
  const today = utcDate(now.getFullYear(), now.getMonth() + 1, now.getDate());
  const lastDay = utcDate(year, month + 1, 0);
  return {
    yearStart: formatDay(utcDate(year, 1, 1)),
    start: formatDay(utcDate(year, month, 1)),
    end: formatDay(lastDay > today ? today : lastDay),
  };
};

const num = (n) => Number(n).toLocaleString('en-US');
const pct = (share) => `${Math.round(share * 100)}%`;
const plural = (n, one, many) => `${num(n)} ${n === 1 ? one : many}`;
const users = (n) => plural(n, 'user', 'users');

const b = (text) => ({ text, bold: true });
const runs = (...parts) => parts.filter(Boolean).map((part) => (typeof part === 'string' ? { text: part } : part));
const item = (itemRuns, children) => (children?.length ? { runs: itemRuns, children } : { runs: itemRuns });
const paragraph = (...parts) => ({ type: 'paragraph', runs: runs(...parts) });

const topShare = (share, who, what) =>
  share == null ? null : ` (the 10% of ${who} this month accounted for ${pct(share)} of ${what})`;

export const buildMonthlyReport = (data, { submittedBy } = {}) => {
  const { registrations: reg, active_users: active, clara, pieces } = data;
  const { peer_reviews: reviews, comments, reads, likes } = data;
  const period = reportPeriod(data.month);
  const previousMonth = monthName(data.previous_month);

  const blocks = [{ type: 'title', text: `Write the World Data Report: ${formatMonth(data.month)}` }];
  if (submittedBy?.trim()) {
    blocks.push({ type: 'subtitle', text: `Submitted by ${submittedBy.trim()}` });
  }

  // Registrations and logins
  blocks.push(paragraph(`From ${period.start} - ${period.end},`), {
    type: 'list',
    items: [
      item(runs(b(`${num(reg.young_writers)} new young writers registered on the site compared to ${num(reg.previous_young_writers)} in ${previousMonth}.`))),
      item(runs(b(`${num(reg.educators)} new educators registered on the site compared to ${num(reg.previous_educators)} in ${previousMonth}.`))),
      reg.non_us_share != null && item(runs(b(`${pct(reg.non_us_share)} of new users came from outside of the US.`))),
      item(runs(b(`There were ${num(active.young_writers)} young writers and ${num(active.educators)} educators active (logged in at least once during the month).`))),
      item(runs(b(`There were ${plural(clara.users, 'Clara user', 'Clara users')} who invited Clara to ${plural(clara.pieces, 'piece', 'pieces')}.`))),
    ].filter(Boolean),
  });

  // Competitions that received submissions this month
  data.competitions.forEach((c) => {
    blocks.push(paragraph(
      `In the ${c.title}, `,
      b(`${plural(c.submissions, 'unique piece was', 'unique pieces were')} submitted`),
      ' representing ',
      b(plural(c.countries, 'country', 'countries')),
      ', and there were ',
      b(plural(c.peer_reviews, 'peer review', 'peer reviews')),
      ' of competition pieces published.'
    ));
  });

  // Site activity
  const pieceDetails = [];
  if (pieces.average != null) {
    const median = Number.isInteger(pieces.median) ? pieces.median : pieces.median.toFixed(1);
    pieceDetails.push(item(runs(
      'Of users who published pieces, they published ',
      b(`an average of ${pieces.average.toFixed(1)} pieces and a median of ${median} pieces.`)
    )));
  }
  if (data.top_prompts.length) {
    pieceDetails.push(item(
      runs(`Top ${data.top_prompts.length} non-competition Prompts:`),
      data.top_prompts.map((p) => item(runs(b(num(p.pieces)), `  ${p.title}`)))
    ));
  }

  blocks.push(paragraph(`In the month of ${formatMonth(data.month)},`), {
    type: 'list',
    items: [
      item(runs(
        'a total of ',
        b(`${plural(pieces.total, 'piece was', 'pieces were')} published by ${users(pieces.users)}`),
        '.',
        topShare(pieces.top10_share, 'writers who published the most pieces', 'pieces')
      ), pieceDetails),
      item(runs(
        'There were ',
        b(`${plural(reviews.total, 'peer review', 'peer reviews')} shared by ${users(reviews.users)}.`),
        topShare(reviews.top10_share, 'peer reviewers who shared the most reviews', 'reviews')
      )),
      item(runs(
        'There were ',
        b(`${plural(comments.total, 'comment', 'comments')} posted by ${users(comments.users)}`),
        topShare(comments.top10_share, 'commenters who posted the most comments', 'comments')
      )),
      item(runs(
        b(`${plural(reads.total, 'piece read', 'piece reads')} (excluding subsequent reads of the same piece by the same user) by ${users(reads.users)}.`),
        topShare(reads.top10_share, 'readers who read the most pieces', 'reads')
      )),
      item(runs(
        'There were ',
        b(`${plural(likes.total, 'like', 'likes')} by ${users(likes.users)}`),
        topShare(likes.top10_share, 'users who liked the most pieces', 'likes')
      )),
      item(runs('There were ', b(`${plural(data.collections_created.total, 'collection', 'collections')} created by ${users(data.collections_created.users)}.`))),
      item(runs('There were ', b(`${plural(data.collections_followed.total, 'collection', 'collections')} followed by ${users(data.collections_followed.users)}.`))),
    ],
  });

  // "How did you hear about us"
  if (data.referrals.length) {
    blocks.push(
      paragraph(`A summary of “how did you hear about us” responses of users who joined during the period ${period.start} - ${period.end} is given hereby:`),
      { type: 'table', widths: [0.45, 0.15], rows: data.referrals.map((r) => [r.source, num(r.users)]) }
    );
  }

  // Educators from domains with many sign-ups
  blocks.push(paragraph(`List of educators from email domains with 4+ users who registered from ${period.yearStart} - ${period.end}:`));
  if (data.educators.length) {
    blocks.push({
      type: 'table',
      small: true,
      widths: [0.22, 0.36, 0.14, 0.14, 0.14],
      header: ['Name', 'Email', 'Users from domain', 'Joined', 'Last seen'],
      rows: data.educators.map((e) => [e.full_name ?? '', e.email ?? '', num(e.user_number), e.created_at ?? '', e.last_seen_at ?? '']),
    });
  } else {
    blocks.push(paragraph('No email domains had 4+ users register in this period.'));
  }

  return blocks;
};
